"use client"

import { isChannelVisibleInSettings } from "@/lib/agents/messaging/channels/shared/channel-display"
import { useState } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Skeleton } from "@/components/ui/skeleton"
import { Alert, AlertDescription } from "@/components/ui/alert"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Loader2, Trash2, MessageCircle, Settings, Globe, ExternalLink, X, RefreshCw,
  ChevronDown, ChevronUp, Code2,
} from "lucide-react"
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible"
import { ChannelIcon } from "@/components/ui/channel-icon"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { toast } from "sonner"
import { usePermissions } from "@/hooks/use-permissions"
import { DEMO_MODE_PERMISSIONS } from "@/lib/auth/permissions.constants"
import { openOAuthPopup } from "@/lib/oauth/popup"
import type { MessageAgentWithChannels, MessageAgentChannel } from "@/hooks/agents/messaging/use-agents"
import {
  CHANNEL_STATUS_COLORS,
  getChannelExternalUrl,
  sortChannelsForDisplay,
} from "../constants"
import { CHANNEL_LABELS, isAiReplyEnabled } from "@/lib/agents/messaging/types"
import { WebWidgetSettingsDialog } from "./web-widget-settings-dialog"
import { WebWidgetColorPicker } from "./web-widget-color-picker"
import { WebDemoSettings } from "./web-demo-settings"
import { WidgetInstallInstructions } from "./widget-install-instructions"
import { TelegramConnectDialog } from "./telegram-connect-dialog"
import { WhatsappNumberDialog } from "./whatsapp-number-dialog"
import { WhatsappConnectNoticeDialog } from "./whatsapp-connect-notice-dialog"
import { TiktokConnectNoticeDialog } from "./tiktok-connect-notice-dialog"
import { useWhatsappEmbeddedSignup } from "@/hooks/use-whatsapp-embedded-signup"
import { useTiktokConnectConfigQuery } from "@/hooks/queries/tiktok-connect-config"
import { useStartTiktokAuthMutation } from "@/hooks/mutations/tiktok-connect"
import {
  TIKTOK_AUDIENCE_LIMIT_BADGE,
  TIKTOK_AUDIENCE_LIMIT_TOOLTIP,
} from "@/lib/agents/messaging/channels/tiktok/regional-notice"
import { Switch } from "@/components/ui/switch"
import { ChannelOfflineMessageControl } from "./channel-offline-message-control"
import { AiReplyOffBadge, AiReplyOffHint } from "../ai-reply-off-badge"
import {
  useConnectSimpleMessagingChannelMutation,
  useDeleteMessagingChannelMutation,
  useStartMetaMessagingChannelMutation,
  useUpdateMessagingChannelAiRepliesMutation,
} from "@/hooks/mutations/messaging-channels"
import { useTranslations } from "next-intl"




interface ChannelSettingsProps {
  agent: MessageAgentWithChannels
  connectedChannelsLoading?: boolean
  onRefresh: () => void
  canManage: boolean
}

export function ChannelSettings({
  agent,
  connectedChannelsLoading = false,
  onRefresh,
  canManage,
}: ChannelSettingsProps) {


  const tUi = useTranslations("Messaging.AgentsMessagingSettingsChannelSettings")
  const tCommonUi = useTranslations("Common")

  const { can } = usePermissions()
  const canManageDemo = can(DEMO_MODE_PERMISSIONS.MANAGE)
  const [isConnecting, setIsConnecting] = useState<string | null>(null)
  const [deleteChannel, setDeleteChannel] = useState<MessageAgentChannel | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [widgetSettingsOpen, setWidgetSettingsOpen] = useState(false)
  const [demoSettingsOpen, setDemoSettingsOpen] = useState(false)
  const [telegramDialogOpen, setTelegramDialogOpen] = useState(false)
  const [whatsappNumberChannel, setWhatsappNumberChannel] = useState<MessageAgentChannel | null>(null)
  const [whatsappNoticeOpen, setWhatsappNoticeOpen] = useState(false)
  const [tiktokNoticeOpen, setTiktokNoticeOpen] = useState(false)
  const [aiToggleChannelId, setAiToggleChannelId] = useState<string | null>(null)
  // Open by default — the snippet is what a new widget owner comes here for; tracking the
  // collapsed row keeps that default without deriving state from the channel list.
  const [installClosedChannelId, setInstallClosedChannelId] = useState<string | null>(null)
  const startMetaChannel = useStartMetaMessagingChannelMutation()
  const connectSimpleChannel = useConnectSimpleMessagingChannelMutation()
  const deleteChannelMutation = useDeleteMessagingChannelMutation()
  const updateAiReplies = useUpdateMessagingChannelAiRepliesMutation()
  const isDeleting = deleteChannelMutation.isPending

  const handleToggleAiReplies = async (channel: MessageAgentChannel, enabled: boolean) => {
    setError(null)
    setAiToggleChannelId(channel.id)
    try {
      await updateAiReplies.mutateAsync({
        agentId: agent.id,
        channelId: channel.id,
        aiRepliesEnabled: enabled,
      })
      onRefresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : tUi("failedToUpdateAIReplies"))
    } finally {
      setAiToggleChannelId(null)
    }
  }

  const allChannels = agent.channels || []
  // Hide pending disconnected slots left by an abandoned OAuth. WhatsApp is the
  // exception: Embedded Signup only ever writes a row on success, so a
  // disconnected WhatsApp row is a real channel Meta offboarded — hiding it
  // would leave the agent's slot occupied by something the user cannot see,
  // reconnect or remove.
  const channels = sortChannelsForDisplay(
    allChannels.filter((c) => isChannelVisibleInSettings(c.channel_type, c.status)),
  )

  const handleConnectChannel = async (channelType: "instagram" | "facebook") => {
    setError(null)
    setIsConnecting(channelType)
    try {
      const data = await startMetaChannel.mutateAsync({
        agentId: agent.id,
        channelType,
      })
      const label = channelType === "instagram" ? "Instagram" : "Facebook Messenger"

      openOAuthPopup({
        url: String(data.authorizeUrl),
        name: `meta_${channelType}_oauth`,
        onMessage: (result) => {
          if (result.channel_connected) {
            toast.success(tUi("value0ConnectedSuccessfully", { value0: String(label) }))
          }
          if (result.error) {
            setError(result.error_message ? String(result.error_message) : String(result.error))
          }
          onRefresh()
        },
        onClose: () => onRefresh(),
        errorKey: "error",
        onError: (err) => {
          // Fallback for cases where onMessage is not called (e.g. blocked popup).
          // Skip refresh — onMessage already called it for BroadcastChannel errors.
          setError((prev) => prev || err)
        },
      })
    } catch {
      setError(`Failed to connect ${channelType}`)
    } finally {
      setIsConnecting(null)
    }
  }

  const handleDeleteChannel = async () => {
    if (!deleteChannel) return
    setError(null)

    try {
      await deleteChannelMutation.mutateAsync({
        agentId: agent.id,
        channelId: deleteChannel.id,
      })
      setDeleteChannel(null)
      onRefresh()
    } catch (err) {
      setError(err instanceof Error ? err.message : tUi("failedToRemoveChannel"))
    }
  }

  // Only consider connected/error/expired channels as "taken" — disconnected slots can be re-initiated
  const hasInstagram = channels.some((c) => c.channel_type === "instagram")
  const hasFacebook = channels.some((c) => c.channel_type === "facebook")
  const hasTelegram = channels.some((c) => c.channel_type === "telegram")
  const hasWhatsapp = channels.some((c) => c.channel_type === "whatsapp")
  const hasTiktok = channels.some((c) => c.channel_type === "tiktok")
  const hasWebWidget = channels.some((c) => c.channel_type === "web_widget")
  const hasWebDemo = channels.some((c) => c.channel_type === "web_demo")

  // WhatsApp Embedded Signup (self-serve). MVP: one number per agent, so the
  // tile is gated on hasWhatsapp like the other single-slot channels.
  const whatsappSignup = useWhatsappEmbeddedSignup({
    agentId: agent.id,
    onConnected: onRefresh,
    onError: setError,
  })

  // TikTok pre-GA: the config query answers configured:false for anyone who may
  // not onboard it yet, which is what makes the tile read "Coming soon".
  const tiktokConfig = useTiktokConnectConfigQuery()
  const startTiktokAuth = useStartTiktokAuthMutation()

  const handleConnectTiktok = async () => {
    setError(null)
    setIsConnecting("tiktok")
    try {
      const data = await startTiktokAuth.mutateAsync({ agentId: agent.id })
      openOAuthPopup({
        url: data.authorizeUrl,
        name: "tiktok_oauth",
        onMessage: (result) => {
          if (result.channel_connected) {
            toast.success(tUi("tikTokConnectedSuccessfully"))
          }
          if (result.error) {
            setError(result.error_message ? String(result.error_message) : String(result.error))
          }
          onRefresh()
        },
        onClose: () => onRefresh(),
        errorKey: "error",
        onError: (err) => setError((prev) => prev || err),
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : tUi("failedToConnectTikTok"))
    } finally {
      setIsConnecting(null)
    }
  }

  const handleConnectWebDemo = async () => {
    setError(null)
    setIsConnecting("web_demo")
    try {
      await connectSimpleChannel.mutateAsync({
        agentId: agent.id,
        channelType: "web-demo",
      })

      toast.success(tUi("demoWidgetConnectedSuccessfully"))
      onRefresh()
    } catch {
      setError(tUi("failedToConnectDemoWidget"))
    } finally {
      setIsConnecting(null)
    }
  }

  const handleConnectWebWidget = async () => {
    setError(null)
    setIsConnecting("web_widget")
    try {
      await connectSimpleChannel.mutateAsync({
        agentId: agent.id,
        channelType: "web-widget",
      })

      toast.success(tUi("webChatConnectedSuccessfully"))
      onRefresh()
    } catch {
      setError(tUi("failedToConnectWebChat"))
    } finally {
      setIsConnecting(null)
    }
  }

  return (
    <div className="space-y-6">
      {error && (
        <Alert className="border-red-200 bg-red-50 pr-10 relative">
          <AlertDescription className="text-red-800">{error}</AlertDescription>
          <button
            type="button"
            aria-label={tUi("dismissError")}
            onClick={() => setError(null)}
            className="absolute top-2.5 right-2.5 rounded p-0.5 text-red-500 hover:text-red-800 hover:bg-red-100"
          >
            <X className="h-4 w-4" />
          </button>
        </Alert>
      )}
      {channels.some(channel => ['instagram', 'facebook', 'telegram', 'whatsapp'].includes(channel.channel_type)) && (
        <p className="text-sm text-muted-foreground">
          <span key={tUi("ifYourMessengerAccountSupportsAProfileOr")}>{tUi("ifYourMessengerAccountSupportsAProfileOr")}</span></p>
      )}

      {/* Hide the demo channel entirely from users without `demo_mode.manage`.
          The card, its settings icon, and the delete button all disappear —
          consistent with the "Add Channel" block which never offers Demo
          Widget to those users in the first place. */}
      {(() => {
      const visibleChannels = channels.filter(
        (ch) => ch.channel_type !== "web_demo" || canManageDemo,
      )
      const agentReplyOff = !isAiReplyEnabled(agent.config)
      return (
      <Card className="relative min-h-32" aria-busy={connectedChannelsLoading}>
        {connectedChannelsLoading ? (
          <div
            role="status"
            className="absolute inset-0 z-10 flex items-center justify-center gap-2 rounded-xl bg-background/85 text-sm text-muted-foreground backdrop-blur-[1px]"
          >
            <Loader2 className="h-5 w-5 animate-spin" />
            <span><span key={tUi("refreshingConnectedChannels")}>{tUi("refreshingConnectedChannels")}</span></span>
          </div>
        ) : null}
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span><span key={tUi("connectedChannels")}>{tUi("connectedChannels")}</span></span>
            {/* The per-channel switches below are moot while the agent-wide one is off. */}
            {agentReplyOff && <AiReplyOffBadge className="text-xs" />}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {visibleChannels.length === 0 ? (
            <p className="text-sm text-gray-500"><span key={tUi("noChannelsConnectedConnectAChannelToStart")}>{tUi("noChannelsConnectedConnectAChannelToStart")}</span></p>
          ) : (
            <div className="space-y-3">
              {visibleChannels.map((ch) => {
                const url = getChannelExternalUrl(ch)
                const name = ch.account_name || CHANNEL_LABELS[ch.channel_type]
                return (
                <div key={ch.id} className="rounded-lg border">
                  <div
                    className={`flex flex-wrap gap-3 p-3 ${
                      ch.channel_type === "web_widget"
                        ? "flex-col sm:flex-row sm:items-center sm:justify-between"
                        : "items-center justify-between"
                    }`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                    {/* Avatar (profile picture) with a brand badge, or the brand
                        icon itself when no avatar. Connection status is conveyed
                        by the dot next to the name and the status badge. */}
                    <div className="relative shrink-0">
                      {ch.config?.profile_picture_url ? (
                        <>
                          <Avatar className="h-9 w-9">
                            <AvatarImage src={ch.config.profile_picture_url as string} />
                            <AvatarFallback className="text-xs">
                              {(ch.account_name || '?').slice(0, 2).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <span className="absolute -bottom-1 -right-1 rounded-full bg-white p-0.5 ring-1 ring-gray-200">
                            <ChannelIcon channelType={ch.channel_type} size={12} />
                          </span>
                        </>
                      ) : (
                        <div className="h-9 w-9 flex items-center justify-center rounded-full bg-gray-50 ring-1 ring-gray-200">
                          <ChannelIcon channelType={ch.channel_type} size={18} />
                        </div>
                      )}
                    </div>
                    <div className="min-w-0">
                      <div className="font-medium text-sm flex items-center gap-2">
                        {url ? (
                          <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={tUi("openValue0", { value0: String(name) })}
                            className="inline-flex items-center gap-1 hover:text-blue-600 hover:underline truncate"
                          >
                            <span className="truncate">{name}</span>
                            <ExternalLink className="w-3 h-3 shrink-0" />
                          </a>
                        ) : (
                          <span className="truncate">{name}</span>
                        )}
                        {ch.status === "connected" && (
                          <span
                            role="img"
                            aria-label={tUi("connected")}
                            title={tUi("connected")}
                            className="h-2 w-2 rounded-full bg-green-500 inline-block shrink-0"
                          />
                        )}
                        {(ch.status === "error" || ch.status === "token_expired") && (
                          <span
                            role="img"
                            aria-label={ch.status.replace("_", " ")}
                            title={ch.status.replace("_", " ")}
                            className="h-2 w-2 rounded-full bg-red-500 inline-block shrink-0"
                          />
                        )}
                      </div>
                      <div className="text-xs text-gray-500 flex items-center gap-1.5 flex-wrap">
                        <span>
                          {CHANNEL_LABELS[ch.channel_type]}
                          {typeof ch.config?.display_name === 'string' && ` · ${ch.config.display_name}`}
                          {ch.external_account_id && ch.channel_type !== "web_widget" && ch.channel_type !== "web_demo" && ` · ID: ${ch.external_account_id}`}
                        </span>
                        {/* The support path differs: never re-registered, no PIN of ours. */}
                        {ch.config?.coexistence === true && (
                          <Badge variant="outline" className="text-[10px] font-normal py-0">
                            <span key={tUi("coexistence")}>{tUi("coexistence")}</span></Badge>
                        )}
                        {/* Permanent, not a setup step: TikTok delivers EEA/CH/UK
                            messages with no content, so part of the audience can
                            never be answered here. Kept on the row rather than only
                            in the pre-connect dialog nobody reopens. */}
                        {ch.channel_type === "tiktok" && (
                          <Badge
                            variant="outline"
                            className="text-[10px] font-normal py-0"
                            title={TIKTOK_AUDIENCE_LIMIT_TOOLTIP}
                          >
                            {TIKTOK_AUDIENCE_LIMIT_BADGE}
                          </Badge>
                        )}
                      </div>
                      {ch.last_error && (
                        <div className="text-xs text-red-500 mt-1">{ch.last_error}</div>
                      )}
                    </div>
                    </div>
                    <div
                      className={`flex max-w-full flex-wrap items-center gap-2 ${
                        ch.channel_type === "web_widget"
                          ? "w-full justify-between border-t pt-3 sm:w-auto sm:justify-end sm:border-0 sm:pt-0"
                          : ""
                      }`}
                    >
                      {/* Channel-specific actions lead the group, so the AI switch and the offline
                          chip line up across every row. */}
                      {ch.channel_type === "web_demo" && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDemoSettingsOpen(true)}
                          title={tUi("demoSettings")}
                        >
                          <Settings className="w-4 h-4" />
                        </Button>
                      )}
                      {/* Recovery path for 131037: an approved display name is not
                          applied until the number is registered again. */}
                      {ch.channel_type === "whatsapp" && canManage && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setWhatsappNumberChannel(ch)}
                          title={tUi("refreshNumber")}
                        >
                          <RefreshCw className="w-4 h-4" />
                        </Button>
                      )}
                      {/* While the agent-wide switch is off the row explains itself through the
                          shared tooltip; `title` would run off the window edge on this column. */}
                      {ch.status === "connected" && (
                        <ChannelAiReplyLabel agentReplyOff={agentReplyOff}>
                        <label
                          className={`flex items-center gap-1.5 text-xs ${
                            ch.ai_replies_enabled ? "text-muted-foreground" : "font-medium text-amber-700"
                          } ${agentReplyOff ? "opacity-50" : ""}`}
                          title={
                            agentReplyOff
                              ? undefined
                              : tUi("stopAIRepliesOnThisChannelMessagesKeep")
                          }
                        >
                          <span key={ch.ai_replies_enabled ? tUi("aIReplies") : tUi("aIRepliesOff")} className="hidden lg:inline">
                            {ch.ai_replies_enabled ? tUi("aIReplies") : tUi("aIRepliesOff")}
                          </span>
                          <Switch
                            checked={ch.ai_replies_enabled}
                            disabled={
                              aiToggleChannelId === ch.id ||
                              !(ch.channel_type === "web_demo" ? canManageDemo : canManage)
                            }
                            onCheckedChange={(checked) => handleToggleAiReplies(ch, checked)}
                            aria-label={tUi("aIReplies")}
                          />
                        </label>
                        </ChannelAiReplyLabel>
                      )}
                      {ch.status === "connected" && ch.channel_type !== "web_demo" && (
                        <ChannelOfflineMessageControl
                          agentId={agent.id}
                          channel={ch}
                          canManage={canManage}
                          onRefresh={onRefresh}
                          onError={setError}
                        />
                      )}
                      {/* The green dot beside the name already says "connected"; spell out only
                          the states that demand attention. */}
                      {ch.status !== "connected" && (
                        <Badge className={CHANNEL_STATUS_COLORS[ch.status] || "bg-gray-100 text-gray-800"}>
                          {ch.status.replace("_", " ")}
                        </Badge>
                      )}
                      {canManage && ch.status !== "connected" && (ch.channel_type === "instagram" || ch.channel_type === "facebook") && (
                        <Button variant="outline" size="sm" onClick={() => handleConnectChannel(ch.channel_type as "instagram" | "facebook")} disabled={!!isConnecting}>
                          <span key={tUi("reconnect")}>{tUi("reconnect")}</span></Button>
                      )}
                      {/* A connected channel can still lack template access. Reauthorize
                          the same number in place, preserving its conversation history. */}
                      {canManage && ch.channel_type === "whatsapp" && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setError(null)
                            whatsappSignup.launch(true)
                          }}
                          disabled={!!isConnecting || whatsappSignup.connecting || !whatsappSignup.ready}
                        >
                          <span><span key={tUi("reconnect")}>{tUi("reconnect")}</span></span>
                        </Button>
                      )}
                      {/* Reconnect reuses the row, so the conversation history of
                          this TikTok account survives a re-authorization. */}
                      {canManage && ch.status !== "connected" && ch.channel_type === "tiktok" && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setError(null)
                            setTiktokNoticeOpen(true)
                          }}
                          disabled={!!isConnecting || !tiktokConfig.data?.configured}
                        >
                          <span key={tUi("reconnect")}>{tUi("reconnect")}</span></Button>
                      )}
                      {(ch.channel_type === 'web_demo' ? canManageDemo : canManage) && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteChannel(ch)}
                          className="text-red-600 hover:bg-red-50 hover:text-red-700"
                          title={tUi("deleteChannel")}
                          aria-label={tUi("deleteChannel")}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                  {/* Install code is the widget's whole setup: open by default, but kept compact
                      so it does not dwarf the other channel rows. */}
                  {ch.channel_type === "web_widget" ? (
                    <Collapsible
                      open={installClosedChannelId !== ch.id}
                      onOpenChange={(open) => setInstallClosedChannelId(open ? null : ch.id)}
                    >
                      <div className="flex flex-col gap-2 border-t bg-muted/20 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:py-2.5">
                        <CollapsibleTrigger asChild>
                          <Button variant="outline" size="sm" className="h-9 w-full justify-between gap-1.5 px-3 text-xs font-medium sm:h-8 sm:w-auto">
                            <Code2 className="h-3.5 w-3.5" />
                            <span><span key={tUi("installCodeOnYourSite")}>{tUi("installCodeOnYourSite")}</span></span>
                            {installClosedChannelId === ch.id ? (
                              <ChevronDown className="h-3.5 w-3.5 opacity-60" />
                            ) : (
                              <ChevronUp className="h-3.5 w-3.5 opacity-60" />
                            )}
                          </Button>
                        </CollapsibleTrigger>
                        <div className="flex items-center justify-between gap-2 sm:justify-end">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-8 gap-1.5 px-3 text-xs"
                          onClick={() => setWidgetSettingsOpen(true)}
                        >
                          <Settings className="h-3.5 w-3.5" />
                          <span><span key={tUi("configureBranding")}>{tUi("configureBranding")}</span></span>
                        </Button>
                        <WebWidgetColorPicker
                          channelId={ch.id}
                          primaryColor={
                            typeof ch.config?.primaryColor === "string"
                              ? ch.config.primaryColor
                              : undefined
                          }
                          canManage={canManage}
                          onRefresh={onRefresh}
                          onError={setError}
                        />
                        </div>
                      </div>
                      <CollapsibleContent className="border-t bg-muted/20 px-4 py-3">
                        {ch.external_account_id ? (
                          <WidgetInstallInstructions token={ch.external_account_id} channelId={ch.id} canManage={canManage} />
                        ) : (
                          <p className="text-xs text-amber-700">
                            <span key={tUi("theInstallationCodeIsUnavailableRefreshTheChannel")}>{tUi("theInstallationCodeIsUnavailableRefreshTheChannel")}</span></p>
                        )}
                      </CollapsibleContent>
                    </Collapsible>
                  ) : null}
                </div>
                )
              })}
            </div>
          )}
        </CardContent>
      </Card>
      )
      })()}

      {/* Web Widget Settings Dialog */}
      {channels.find((c) => c.channel_type === "web_widget") && (
        <WebWidgetSettingsDialog open={widgetSettingsOpen} onOpenChange={setWidgetSettingsOpen}
              onConnect={provider => {
                setWidgetSettingsOpen(false)
                switch (provider) {
                  case 'telegram': setTelegramDialogOpen(true); break
                  case 'whatsapp': setWhatsappNoticeOpen(true); break
                  case 'tiktok': setTiktokNoticeOpen(true); break
                  case 'instagram': case 'facebook': void handleConnectChannel(provider); break
                  default: break
                }
              }}
              channel={channels.find((c) => c.channel_type === "web_widget")!}
              onRefresh={onRefresh}
              canManage={canManage}
        />
      )}

      {/* Web Demo Settings Dialog — only mount when the user can manage demo. */}
      {canManageDemo && channels.find((c) => c.channel_type === "web_demo") && (
        <Dialog open={demoSettingsOpen} onOpenChange={setDemoSettingsOpen}>
          <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle><span key={tUi("demoWidgetSettings")}>{tUi("demoWidgetSettings")}</span></DialogTitle>
            </DialogHeader>
            <WebDemoSettings
              channel={channels.find((c) => c.channel_type === "web_demo")!}
              onRefresh={onRefresh}
              canManage={canManageDemo}
            />
          </DialogContent>
        </Dialog>
      )}

      {(canManage || canManageDemo) && (
        <Card>
          <CardHeader>
            <CardTitle><span key={tUi("addChannel")}>{tUi("addChannel")}</span></CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Button
                variant="outline"
                className="justify-start h-auto p-4"
                onClick={() => handleConnectChannel("instagram")}
                disabled={!!isConnecting || hasInstagram || !canManage}
              >
                {isConnecting === "instagram" ? (
                  <Loader2 className="w-5 h-5 mr-3 animate-spin" />
                ) : (
                  <ChannelIcon channelType="instagram" size={20} className="mr-3" />
                )}
                <div className="text-left">
                  <div className="font-medium"><span key={"Instagram"}>{"Instagram"}</span></div>
                  <div key={hasInstagram ? tUi("alreadyConnected") : !canManage ? tUi("requiresMessagingAgentsManagePermission") : tUi("connectViaMetaOAuth")} className="text-xs text-gray-500">
                    {hasInstagram ? tUi("alreadyConnected") : !canManage ? tUi("requiresMessagingAgentsManagePermission") : tUi("connectViaMetaOAuth")}
                  </div>
                </div>
              </Button>

              <Button
                variant="outline"
                className="justify-start h-auto p-4"
                onClick={() => handleConnectChannel("facebook")}
                disabled={!!isConnecting || hasFacebook || !canManage}
              >
                {isConnecting === "facebook" ? (
                  <Loader2 className="w-5 h-5 mr-3 animate-spin" />
                ) : (
                  <ChannelIcon channelType="facebook" size={20} className="mr-3" />
                )}
                <div className="text-left">
                  <div className="font-medium"><span key={"Facebook Messenger"}>{"Facebook Messenger"}</span></div>
                  <div key={hasFacebook ? tUi("alreadyConnected") : !canManage ? tUi("requiresMessagingAgentsManagePermission") : tUi("connectViaMetaOAuth")} className="text-xs text-gray-500">
                    {hasFacebook ? tUi("alreadyConnected") : !canManage ? tUi("requiresMessagingAgentsManagePermission") : tUi("connectViaMetaOAuth")}
                  </div>
                </div>
              </Button>

              <Button
                variant="outline"
                className="justify-start h-auto p-4"
                onClick={() => setTelegramDialogOpen(true)}
                disabled={!!isConnecting || hasTelegram || !canManage}
              >
                <ChannelIcon channelType="telegram" size={20} className="mr-3" />
                <div className="text-left">
                  <div className="font-medium"><span key={"Telegram"}>{"Telegram"}</span></div>
                  <div key={hasTelegram ? tUi("alreadyConnected") : !canManage ? tUi("requiresMessagingAgentsManagePermission") : tUi("connectABotFromBotFather")} className="text-xs text-gray-500">
                    {hasTelegram ? tUi("alreadyConnected") : !canManage ? tUi("requiresMessagingAgentsManagePermission") : tUi("connectABotFromBotFather")}
                  </div>
                </div>
              </Button>

              {/* The config action returns configured:false when Meta credentials
                  are unset, so this tile reads "Coming soon" and stays disabled.
                  Disabled until `ready` — launch() is a no-op before that, and an
                  enabled tile flashing off on config load reads as a glitch. */}
              <div className="relative">
                <Button
                  variant="outline"
                  className="justify-start h-auto p-4 w-full"
                  onClick={() => {
                    setError(null)
                    whatsappSignup.launch()
                  }}
                  disabled={
                    !!isConnecting ||
                    hasWhatsapp ||
                    !canManage ||
                    whatsappSignup.connecting ||
                    !whatsappSignup.ready
                  }
                >
                  {whatsappSignup.connecting ? (
                    <Loader2 className="w-5 h-5 mr-3 animate-spin" />
                  ) : (
                    <ChannelIcon channelType="whatsapp" size={20} className="mr-3" />
                  )}
                  <div className="text-left">
                    <div className="font-medium"><span key={"WhatsApp"}>{"WhatsApp"}</span></div>
                    {/* No subtitle text until the config resolves — a skeleton avoids
                        flashing a state that flips a second later. */}
                    {!hasWhatsapp && canManage && !whatsappSignup.configLoaded ? (
                      <Skeleton className="h-4 w-28 mt-0.5" />
                    ) : (
                      <div key={hasWhatsapp
                          ? tUi("alreadyConnected")
                          : !canManage
                            ? tUi("requiresMessagingAgentsManagePermission")
                            : whatsappSignup.configured
                              ? tUi("connectANumber")
                              : tUi("comingSoon")} className="text-xs text-gray-500">
                        {hasWhatsapp
                          ? tUi("alreadyConnected")
                          : !canManage
                            ? tUi("requiresMessagingAgentsManagePermission")
                            : whatsappSignup.configured
                              ? tUi("connectANumber")
                              : tUi("comingSoon")}
                      </div>
                    )}
                  </div>
                </Button>
                {/* Sibling overlay, not a child of the Button — nested interactive
                    elements are invalid HTML and would trigger the tile's onClick. */}
                {canManage && whatsappSignup.configured && (
                  <button
                    type="button"
                    className="absolute bottom-1.5 right-2.5 text-[10px] leading-none text-gray-400 underline underline-offset-2 hover:text-gray-600"
                    onClick={() => setWhatsappNoticeOpen(true)}
                  >
                    <span key={tUi("limitationsWhenBringingAnExistingNumber")}>{tUi("limitationsWhenBringingAnExistingNumber")}</span></button>
                )}
              </div>

              {/* Pre-GA the config action returns configured:false for everyone but
                  super users, so this tile reads "Coming soon" and stays disabled. */}
              <Button
                variant="outline"
                className="justify-start h-auto p-4"
                onClick={() => {
                  setError(null)
                  setTiktokNoticeOpen(true)
                }}
                disabled={!!isConnecting || hasTiktok || !canManage || !tiktokConfig.data?.configured}
              >
                {isConnecting === "tiktok" ? (
                  <Loader2 className="w-5 h-5 mr-3 animate-spin" />
                ) : (
                  <ChannelIcon channelType="tiktok" size={20} className="mr-3" />
                )}
                <div className="text-left">
                  <div className="font-medium"><span key={"TikTok"}>{"TikTok"}</span></div>
                  {/* No subtitle until the config resolves — a skeleton avoids
                      flashing a state that flips a second later. */}
                  {!hasTiktok && canManage && tiktokConfig.isLoading ? (
                    <Skeleton className="h-4 w-44 mt-0.5" />
                  ) : (
                    <div key={hasTiktok
                        ? tUi("alreadyConnected")
                        : !canManage
                          ? tUi("requiresMessagingAgentsManagePermission")
                          : tiktokConfig.data?.configured
                            ? tUi("connectATikTokBusinessAccount")
                            : tUi("comingSoon")} className="text-xs text-gray-500">
                      {hasTiktok
                        ? tUi("alreadyConnected")
                        : !canManage
                          ? tUi("requiresMessagingAgentsManagePermission")
                          : tiktokConfig.data?.configured
                            ? tUi("connectATikTokBusinessAccount")
                            : tUi("comingSoon")}
                    </div>
                  )}
                </div>
              </Button>

              <Button
                variant="outline"
                className="justify-start h-auto p-4"
                onClick={handleConnectWebWidget}
                disabled={!!isConnecting || hasWebWidget || !canManage}
              >
                {isConnecting === "web_widget" ? (
                  <Loader2 className="w-5 h-5 mr-3 animate-spin" />
                ) : (
                  <MessageCircle className="w-5 h-5 mr-3" />
                )}
                <div className="text-left">
                  <div className="font-medium"><span key={tUi("webChat")}>{tUi("webChat")}</span></div>
                  <div key={hasWebWidget ? tUi("alreadyConnected") : !canManage ? tUi("requiresMessagingAgentsManagePermission") : tUi("embedOnYourWebsite")} className="text-xs text-gray-500">
                    {hasWebWidget ? tUi("alreadyConnected") : !canManage ? tUi("requiresMessagingAgentsManagePermission") : tUi("embedOnYourWebsite")}
                  </div>
                </div>
              </Button>

              {canManageDemo && (
                <Button
                  variant="outline"
                  className="justify-start h-auto p-4"
                  onClick={handleConnectWebDemo}
                  disabled={!!isConnecting || hasWebDemo}
                >
                  {isConnecting === "web_demo" ? (
                    <Loader2 className="w-5 h-5 mr-3 animate-spin" />
                  ) : (
                    <Globe className="w-5 h-5 mr-3" />
                  )}
                  <div className="text-left">
                    <div className="font-medium"><span key={tUi("demoWidget")}>{tUi("demoWidget")}</span></div>
                    <div key={hasWebDemo ? tUi("alreadyConnected") : tUi("demoChatForLandingPages")} className="text-xs text-gray-500">
                      {hasWebDemo ? tUi("alreadyConnected") : tUi("demoChatForLandingPages")}
                    </div>
                  </div>
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Coexistence constraints — informational, opened from the tile's link */}
      <WhatsappConnectNoticeDialog
        open={whatsappNoticeOpen}
        onOpenChange={setWhatsappNoticeOpen}
      />

      {/* TikTok's regional and conversation limits, shown before authorization */}
      <TiktokConnectNoticeDialog
        open={tiktokNoticeOpen}
        onOpenChange={setTiktokNoticeOpen}
        onContinue={() => {
          setTiktokNoticeOpen(false)
          handleConnectTiktok()
        }}
      />

      {/* WhatsApp number status / display-name recovery */}
      {whatsappNumberChannel && (
        <WhatsappNumberDialog
          channel={whatsappNumberChannel}
          open
          onOpenChange={(next) => !next && setWhatsappNumberChannel(null)}
          onRefreshed={onRefresh}
        />
      )}

      {/* Telegram bot-token connect dialog */}
      <TelegramConnectDialog
        agentId={agent.id}
        open={telegramDialogOpen}
        onOpenChange={setTelegramDialogOpen}
        onConnected={onRefresh}
      />

      {/* Delete confirmation dialog */}
      <AlertDialog open={!!deleteChannel} onOpenChange={() => setDeleteChannel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle><span key={tUi("removeChannel")}>{tUi("removeChannel")}</span></AlertDialogTitle>
            <AlertDialogDescription>
              <span><span key={tUi("thisWillRemoveThe")}>{tUi("thisWillRemoveThe")}</span></span> {deleteChannel && CHANNEL_LABELS[deleteChannel.channel_type]} <span><span key={tUi("channelAndAllItsConversationHistoryThisAction")}>{tUi("channelAndAllItsConversationHistoryThisAction")}</span></span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}><span key={tCommonUi("cancel")}>{tCommonUi("cancel")}</span></AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteChannel}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              <span><span key={tUi("remove")}>{tUi("remove")}</span></span>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

/** Adds the agent-wide explanation to a channel row only while that switch is off. */
function ChannelAiReplyLabel({
  agentReplyOff,
  children,
}: {
  agentReplyOff: boolean
  children: React.ReactElement
}) {
  return agentReplyOff ? <AiReplyOffHint>{children}</AiReplyOffHint> : children
}
